import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Descriptions,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Progress,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  CaretRightOutlined,
  DatabaseOutlined,
  EyeOutlined,
  PauseOutlined,
  ReloadOutlined,
  RocketOutlined,
  StepForwardOutlined,
} from '@ant-design/icons';
import { API_ENDPOINTS } from '../../api/config';
import { get, post } from '../../api/request';
import './InitializationPage.css';

const { Text, Title } = Typography;

type ItemStatus = 'pending' | 'running' | 'failed' | 'skipped' | 'completed';
type TaskStatus = 'queued' | 'running' | 'paused' | 'failed' | 'completed' | 'completed_with_errors';

interface InitializationItem {
  index: number;
  status: ItemStatus;
  stage: string;
  stage_label: string;
  reader: string;
  category: string;
  project_id?: number;
  title: string;
  next_chapter: number;
  error: string;
}

interface InitializationTask {
  id: string;
  status: TaskStatus;
  novel_count: number;
  chapter_count: number;
  words_per_chapter: number;
  completed_count: number;
  failed_count: number;
  skipped_count: number;
  processed_count: number;
  progress: number;
  current_index: number;
  current_stage: string;
  current_title: string;
  error: string;
  recovery_count?: number;
  last_recovered_at?: string;
  items: InitializationItem[];
  created_at: string;
  updated_at: string;
}

interface ListResponse {
  data: InitializationTask[];
  total: number;
}

interface GenerationSettings {
  max_chapters: number;
  default_words_per_chapter: number;
}

const taskStatus = (status: TaskStatus) => {
  const values: Record<TaskStatus, { color: string; label: string }> = {
    queued: { color: 'blue', label: '等待执行' },
    running: { color: 'processing', label: '生成中' },
    paused: { color: 'gold', label: '已暂停' },
    failed: { color: 'error', label: '执行失败' },
    completed: { color: 'success', label: '全部完成' },
    completed_with_errors: { color: 'warning', label: '部分完成' },
  };
  const value = values[status] || values.queued;
  return <Tag color={value.color}>{value.label}</Tag>;
};

const itemStatus = (status: ItemStatus) => {
  const values: Record<ItemStatus, { color: string; label: string }> = {
    pending: { color: 'default', label: '未开始' },
    running: { color: 'processing', label: '进行中' },
    failed: { color: 'error', label: '失败' },
    skipped: { color: 'warning', label: '已跳过' },
    completed: { color: 'success', label: '已发布' },
  };
  const value = values[status] || values.pending;
  return <Tag color={value.color}>{value.label}</Tag>;
};

const formatTime = (value?: string) => value ? new Date(value).toLocaleString('zh-CN', { hour12: false }) : '-';

const InitializationPage: React.FC = () => {
  const [form] = Form.useForm();
  const [tasks, setTasks] = useState<InitializationTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selected, setSelected] = useState<InitializationTask | null>(null);
  const [maxChapters, setMaxChapters] = useState(200);

  const loadTasks = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const response = await get<ListResponse>(API_ENDPOINTS.initialization.tasks);
      setTasks(response.data || []);
      setSelected((current) => current
        ? (response.data || []).find((item) => item.id === current.id) || current
        : current);
    } catch (error) {
      if (!quiet) message.error(error instanceof Error ? error.message : '初始化任务加载失败');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTasks();
    get<GenerationSettings>(API_ENDPOINTS.system_app.generationSettings)
      .then((result) => {
        setMaxChapters(result.max_chapters || 200);
        if (!form.isFieldTouched('words_per_chapter')) {
          form.setFieldValue('words_per_chapter', result.default_words_per_chapter || 1000);
        }
      })
      .catch(() => undefined);
  }, [form, loadTasks]);

  const hasActiveTask = useMemo(
    () => tasks.some((task) => task.status === 'queued' || task.status === 'running'),
    [tasks],
  );

  useEffect(() => {
    if (!hasActiveTask) return undefined;
    const timer = window.setInterval(() => loadTasks(true), 4000);
    return () => window.clearInterval(timer);
  }, [hasActiveTask, loadTasks]);

  const startTask = async () => {
    const values = await form.validateFields();
    let operationPassword = '';
    Modal.confirm({
      title: '验证操作权限',
      content: (
        <div>
          <p>
            将串行生成 {values.novel_count} 本小说，每本 {values.chapter_count} 章。
            任务会持续调用已配置的 AI 服务，请输入操作人员密码确认。
          </p>
          <Input.Password
            autoFocus
            placeholder="请输入操作人员密码"
            onChange={(event) => {
              operationPassword = event.target.value;
            }}
            onPressEnter={(event) => {
              event.preventDefault();
            }}
          />
        </div>
      ),
      okText: '开始生成',
      cancelText: '取消',
      onOk: async () => {
        if (!operationPassword) {
          message.warning('请输入操作人员密码');
          return Promise.reject();
        }
        setSubmitting(true);
        try {
          await post(API_ENDPOINTS.initialization.tasks, {
            ...values,
            operation_password: operationPassword,
          });
          message.success('初始化任务已创建');
          await loadTasks();
        } catch (error) {
          message.error(error instanceof Error ? error.message : '任务创建失败');
          return Promise.reject(error);
        } finally {
          setSubmitting(false);
        }
      },
    });
  };

  const runAction = async (task: InitializationTask, action: 'pause' | 'resume') => {
    try {
      await post(action === 'pause'
        ? API_ENDPOINTS.initialization.pause(task.id)
        : API_ENDPOINTS.initialization.resume(task.id));
      message.success(action === 'pause' ? '已提交暂停请求' : '任务已从断点继续');
      await loadTasks(true);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '操作失败');
    }
  };

  const skipCurrent = (task: InitializationTask) => {
    Modal.confirm({
      title: '跳过当前作品？',
      content: '当前模型调用会在超时或返回后停止处理这本书，已生成的项目和章节会保留，然后自动继续下一本。',
      okText: '跳过并继续',
      cancelText: '取消',
      onOk: async () => {
        try {
          await post(API_ENDPOINTS.initialization.skipCurrent(task.id));
          message.success('已请求跳过当前作品，系统将自动继续下一本');
          await loadTasks(true);
        } catch (error) {
          message.error(error instanceof Error ? error.message : '跳过失败');
        }
      },
    });
  };

  const latest = tasks[0];
  const columns = [
    {
      title: '任务',
      dataIndex: 'id',
      width: 190,
      render: (value: string) => <Text code>{value.slice(0, 12)}</Text>,
    },
    {
      title: '规模',
      width: 180,
      render: (_: unknown, row: InitializationTask) => `${row.novel_count} 本 · ${row.chapter_count} 章/本`,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 110,
      render: taskStatus,
    },
    {
      title: '进度',
      width: 260,
      render: (_: unknown, row: InitializationTask) => (
        <div className="initialization-progress-cell">
          <Progress percent={row.progress || 0} size="small" status={row.status === 'failed' ? 'exception' : undefined} />
          <Text type="secondary">
            已处理 {row.processed_count ?? row.completed_count + row.failed_count}/{row.novel_count} 本
            {row.failed_count ? ` · 失败 ${row.failed_count}` : ''}
            {row.skipped_count ? ` · 跳过 ${row.skipped_count}` : ''}
          </Text>
        </div>
      ),
    },
    {
      title: '当前步骤',
      dataIndex: 'current_stage',
      ellipsis: true,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      width: 180,
      render: formatTime,
    },
    {
      title: '操作',
      width: 350,
      fixed: 'right' as const,
      render: (_: unknown, row: InitializationTask) => (
        <Space>
          <Button icon={<EyeOutlined />} onClick={() => setSelected(row)}>详情</Button>
          {(row.status === 'queued' || row.status === 'running') && (
            <Button icon={<PauseOutlined />} onClick={() => runAction(row, 'pause')}>暂停</Button>
          )}
          {row.status === 'running' && (
            <Button danger icon={<StepForwardOutlined />} onClick={() => skipCurrent(row)}>跳过当前书</Button>
          )}
          {(row.status === 'paused' || row.status === 'failed' || row.status === 'completed_with_errors') && (
            <Button type="primary" icon={<CaretRightOutlined />} onClick={() => runAction(row, 'resume')}>
              {row.status === 'completed_with_errors' ? '重试未完成项' : '继续'}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const itemColumns = [
    { title: '#', dataIndex: 'index', width: 64 },
    { title: '作品', dataIndex: 'title', ellipsis: true, render: (value: string) => value || '等待生成' },
    { title: '频道', dataIndex: 'reader', width: 82, render: (value: string) => value || '-' },
    { title: '分类', dataIndex: 'category', width: 110, render: (value: string) => value || '-' },
    { title: '状态', dataIndex: 'status', width: 92, render: itemStatus },
    { title: '当前步骤', dataIndex: 'stage_label', width: 190, ellipsis: true },
    {
      title: '错误',
      dataIndex: 'error',
      ellipsis: true,
      render: (value: string) => value ? <Text type="danger">{value}</Text> : '-',
    },
  ];

  return (
    <div className="initialization-page">
      <section className="initialization-heading">
        <div>
          <div className="initialization-eyebrow"><DatabaseOutlined /> SYSTEM DATA</div>
          <Title level={2}>初始化数据</Title>
          <Text type="secondary">按启用分类自动完成灵感、创作、封面和发布，为新环境生成可直接浏览的原创小说。</Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={() => loadTasks()} loading={loading}>刷新</Button>
      </section>

      <section className="initialization-create">
        <Form
          form={form}
          layout="vertical"
          initialValues={{ novel_count: 100, chapter_count: 40, words_per_chapter: 1000 }}
        >
          <Form.Item label="小说数量" name="novel_count" rules={[{ required: true }]}>
            <InputNumber min={1} max={500} precision={0} />
          </Form.Item>
          <Form.Item label="每本章节" name="chapter_count" rules={[{ required: true }]} extra={`系统设置上限：${maxChapters} 章`}>
            <InputNumber min={3} max={maxChapters} precision={0} />
          </Form.Item>
          <Form.Item label="每章字数" name="words_per_chapter" rules={[{ required: true }]} extra="默认跟随系统小说生成设置">
            <InputNumber min={500} max={5000} step={100} precision={0} />
          </Form.Item>
          <Button
            type="primary"
            icon={<RocketOutlined />}
            onClick={startTask}
            loading={submitting}
            disabled={hasActiveTask}
          >
            开始生成
          </Button>
        </Form>
        <div className="initialization-live">
          <span className={`initialization-live-dot ${hasActiveTask ? 'is-running' : ''}`} />
          <div>
            <Text strong>{latest ? latest.current_stage : '暂无任务'}</Text>
            <Text type="secondary">{latest?.current_title || '创建任务后将在这里显示实时进度'}</Text>
          </div>
          {latest && <Progress type="circle" size={64} percent={latest.progress || 0} />}
        </div>
      </section>

      <Alert
        type="info"
        showIcon
        message="每本小说会独立完成章节、封面并立即发布；单本失败会自动跳过，批次结束后可从失败作品的断点重试。"
      />

      <section className="initialization-table">
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={tasks}
          scroll={{ x: 1180 }}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
        />
      </section>

      <Drawer
        title="初始化任务详情"
        width={860}
        open={!!selected}
        onClose={() => setSelected(null)}
      >
        {selected && (
          <Space direction="vertical" size={20} style={{ width: '100%' }}>
            <Descriptions column={2} bordered size="small">
              <Descriptions.Item label="任务状态">{taskStatus(selected.status)}</Descriptions.Item>
              <Descriptions.Item label="总体进度">
                已处理 {selected.processed_count ?? selected.completed_count + selected.failed_count}/{selected.novel_count} 本
              </Descriptions.Item>
              <Descriptions.Item label="执行结果">
                成功 {selected.completed_count} 本 · 失败 {selected.failed_count} 本
                {selected.skipped_count ? ` · 跳过 ${selected.skipped_count} 本` : ''}
              </Descriptions.Item>
              <Descriptions.Item label="每本章节">{selected.chapter_count} 章</Descriptions.Item>
              <Descriptions.Item label="每章字数">{selected.words_per_chapter} 字</Descriptions.Item>
              <Descriptions.Item label="当前步骤">{selected.current_stage}</Descriptions.Item>
              <Descriptions.Item label="自动恢复">
                {selected.recovery_count
                  ? `已接管 ${selected.recovery_count} 次 · ${formatTime(selected.last_recovered_at)}`
                  : '未发生服务重启'}
              </Descriptions.Item>
              <Descriptions.Item label="更新时间">{formatTime(selected.updated_at)}</Descriptions.Item>
            </Descriptions>
            {selected.error && (
              <Alert
                type={selected.status === 'completed_with_errors' || selected.status === 'running' ? 'warning' : 'error'}
                showIcon
                message={selected.status === 'completed_with_errors' ? '任务已结束，存在失败作品' : '任务执行提示'}
                description={selected.error}
              />
            )}
            <Table
              rowKey="index"
              size="small"
              columns={itemColumns}
              dataSource={selected.items || []}
              pagination={{ pageSize: 12 }}
              scroll={{ x: 760 }}
            />
          </Space>
        )}
      </Drawer>
    </div>
  );
};

export default InitializationPage;
